import {differenceInMinutes} from 'date-fns';

// Route paths
export const ROUTE_PATHS = {
  HOME: '/',
  LOGIN: '/login',
  ME: '/me',
  APPS: '/apps',
  APPS_NEW: '/apps/new',
  APPS_STUDIO: '/apps/studio',
  APPS_BUILD: '/apps/build',
  APPS_DRAFT: '/apps/drafts/:builderAppId',
  APPS_UPDATE: '/apps/:siteId/update',
  ASK: '/ask',
  SEARCH_APP: '/search',
  PEOPLE: '/people',
  PROFILE: '/people/:userid',
  APRIL_FOOLS_COMPANY_OS_PRO: '/companyos-pro',
  TEAMS: '/teams',
  TEAMS_OWNERSHIP: '/teams/ownership',
  TEAMS_APPS: '/teams/apps',
  TEAMS_APP: '/teams/apps/:serviceId',
  TEAMS_COMPONENTS_AND_SERVICES: '/teams/components-and-services',
  TEAMS_COMPONENTS_AND_SERVICES_COMPONENT: '/teams/components-and-services/:componentId',
  TEAMS_COMPONENTS_AND_SERVICES_SERVICE:
    '/teams/components-and-services/:componentId/services/:serviceId',
  TEAMS_COMPONENTS_AND_SERVICES_REFERENCE:
    '/teams/components-and-services/:componentId/references/:referenceId',
  CHECKUP_PEOPLE_ID: '/people/:id/checkup',
  CHECKUP_TEAM_ID: '/teams/:id/checkup',
  TEAM: '/teams/:slug',
  TEAM_EDIT: '/teams/:slug/edit',
  TEAM_NEW: '/teams/new',
  SETTINGS: '/settings',
  PAPER_PREVIEW: '/scl/fi/:linkId/:filename',
  PLANNING_DOC: '/planning/:id',
  WAITLIST: '/waitlist',
  GRATITUDE_BROWSE: '/gratitude',
  GRATITUDE_INBOX: '/gratitude/inbox',
  GRATITUDE_OUTBOX: '/gratitude/outbox',
  GRATITUDE_LEADERBOARD: '/gratitude/leaderboard',
  LOGOUT: '/logout',
  ADMIN: '/admin',
  ADMIN_APPS: '/admin/apps',
  ADMIN_CELERY_TASKS: '/admin/tasks',
  ADMIN_DCD_JOB_FAMILIES: '/admin/dcd-job-families',
  ADMIN_CARD_MANAGEMENT: '/admin/cards',
  ADMIN_DELEGATES: '/admin/delegates',
  ADMIN_CONFIDENTIAL_METRICS: '/admin/confidential-metrics',
  ADMIN_NOTIFICATIONS: '/admin/notifications',
  ADMIN_OVERDUE_OUTCOMES: '/admin/notifications/overdue-outcomes',
  ADMIN_DOCUMENTS: '/admin/documents',
  ADMIN_KNOWLEDGE_LINKS: '/admin/knowledge-links',
  ADMIN_HOME_BANNERS: '/admin/home-banners',
  ADMIN_PILLARS: '/admin/pillars',
  ADMIN_TIMEFRAMES: '/admin/timeframes',
  ADMIN_TIMEFRAME_ASSIGNMENT: '/admin/timeframes/assignment',
  ADMIN_ROADMAP_JIRA_GROUP_MAPPINGS: '/admin/roadmap/jira-group-mappings',
  ADMIN_SCORECARD_METRIC_DIRECTIONS: '/admin/scorecards/metric-directions',
  ADMIN_WORKSTREAM_AUDIT_LOG: '/admin/workstreams/audit',
  ADMIN_WORKSTREAM_AUTO_ARCHIVE: '/admin/workstreams/auto-archive',
  ADMIN_WORKSTREAM_AUTODRAFT: '/admin/workstreams/autodraft',
  ADMIN_WORKSTREAM_DRAFT_POLICY: '/admin/workstreams/draft-policy',
  ADMIN_WORKSTREAM_IMPORTER: '/admin/workstreams/importer',
  ADMIN_WORKSTREAM_ORPHANS: '/admin/workstreams/orphaned',
  ADMIN_WORKSTREAM_READINESS: '/admin/workstreams/readiness',
  ADMIN_WORKSTREAM_UPDATES: '/admin/workstreams/updates',
  ADMIN_MANAGE_BADGES: '/admin/badges',
  ADMIN_AI_PROMPT: '/admin/ai',
  ADMIN_CONTEXT_ENGINE: '/admin/context-engine',
  ADMIN_AI_VIEW: '/admin/ai-view',
  ADMIN_AMPLITUDE_LOOKUP: '/admin/amplitude-lookup',
  ADMIN_ASK_HEALTH: '/admin/ask-health',
  ADMIN_MIGRATIONS: '/admin/migrations',
  ADMIN_RUNTIME: '/admin/runtime',
  ADMIN_WIREFRAME_DATA: '/admin/wireframe-data',
  DSYS: '/dsys',
  NEWS: '/news',
  NEWS_POST: '/news/:identifier',
  NEWS_NEW: '/news/new',
  NEWS_EDIT: '/news/:identifier/edit',
  // The workstreams page. :id is a workstream or a rail place ('company',
  // 'audit', 'inbox'); :page is 'detail' (the full page) or absent (the focused
  // card). One route, so moving between them keeps the page mounted. Ahead of
  // the /workstreams link paths below so analytics (routeInfo) reports it.
  WORKSTREAMS_PAGE: '/workstreams/:id?/:page?',
  KNOWLEDGE: '/content',
  KNOWLEDGE_CATEGORY: '/content/:category',
  KNOWLEDGE_PAGE: '/content/:category/:id',
  KNOWLEDGE_GUIDES: '/content/guides',
  KNOWLEDGE_GUIDE: '/content/guides/:guideId',
  WORKSTREAMS: '/workstreams',
  ROADMAP: '/roadmap',
  ROADMAP_PRODUCT: '/roadmap/product',
  SCORECARDS: '/scorecards',
  SCORECARDS_DATE: '/scorecards/:date',
  WORKSTREAMS_DETAIL: '/workstreams/:id',
  WORKSTREAM_REPORTS: '/workstreams/:id/reports',
  WORKSTREAM: '/workstream/:id',
  WORKSTREAM_ACTIVITY: '/workstreams/:id/activity',
  WORKSTREAM_NEW: '/workstreams/create',
  WORKSTREAM_EDIT: '/workstreams/:id/edit',
  FEEDBACK: '/feedback',
  FEEDBACK_ALL: '/feedback/*',
};

export const STEAM_TEAM_SLUG = 's-team';

export const STEAM_TEAM_NAME = 'S-team';

export const DBX_OS_TEAM_SLUG = 'dropbox-os-team';

/**
 * Calendar snapshots captured when this module loads, which is normally page load.
 *
 * These values intentionally do not advance while a page remains open.
 * Do not use them for behavior that must change exactly at a quarter or year boundary;
 * existing UI expects users to reload before working in a new calendar period.
 */
export const CURRENT_YEAR = new Date().getFullYear();

export const CURRENT_QUARTER = Math.ceil((new Date().getMonth() + 1) / 3);

const generateTimeframeOptions = (count: number) => {
  const options: {[key: string]: string} = {};
  let quarter = 4;
  let year = 2023;

  for (let i = 0; i < count; i++) {
    // create a date for each next quarter
    const date = new Date();
    date.setDate(1); // we need to set date before setting month since day 31 can be invalid for some months
    date.setMonth((quarter - 1) * 3);
    date.setFullYear(year);
    date.setHours(0, 0, 0, 0);

    const distance = differenceInMinutes(date, new Date());
    let value;

    if (quarter === CURRENT_QUARTER && year === CURRENT_YEAR) {
      // ends in (date of end of quarter
      const endofQuarterDate = new Date(date);
      endofQuarterDate.setMonth(endofQuarterDate.getMonth() + 3);
      endofQuarterDate.setDate(endofQuarterDate.getDate() - 1);
      value = `Ends ${endofQuarterDate.toLocaleString('default', {
        month: 'short',
      })} ${endofQuarterDate.getDate()}`;
    } else if (distance < 0) {
      // ended end of quarter date
      const endofQuarterDate = new Date(date);
      endofQuarterDate.setMonth(endofQuarterDate.getMonth() + 3);
      endofQuarterDate.setDate(endofQuarterDate.getDate() - 1);

      value = `Ended ${endofQuarterDate.toLocaleString('default', {
        month: 'short',
      })} ${endofQuarterDate.getDate()}`;
    } else {
      value = `Starts ${date.toLocaleString('default', {month: 'short'})} ${date.getDate()}`;
    }

    options[`Q${quarter} FY${year - 2000}`] = value;
    quarter = (quarter % 4) + 1;
    if (quarter === 1) {
      year++;
    }
  }

  return options;
};

export const TIMEFRAME_OPTIONS = generateTimeframeOptions(10);

export const DEFAULT_QUARTER = `Q${CURRENT_QUARTER}`;
export const DEFAULT_TIMEFRAME = `Q${CURRENT_QUARTER} FY${CURRENT_YEAR - 2000}`;
type Timeframe = {[key: string]: string};

export const GROUPED_TIMEFRAME_OPTIONS: {
  previous: {[year: string]: Timeframe[]};
  current: Timeframe[];
  upcoming: {[year: string]: Timeframe[]};
} = Object.entries(TIMEFRAME_OPTIONS).reduce(
  (acc: any, [timeframe, date]) => {
    const year = `20${timeframe.match(/FY(\d+)/)?.[1]}`;
    const yearNumber = parseInt(year, 10);

    let type = 'previous';
    if (yearNumber === CURRENT_YEAR) {
      type = 'current';

      acc[type].push({timeframe, date});
      return acc;
    } else if (yearNumber > CURRENT_YEAR) {
      type = 'upcoming';
    }

    // eslint-disable-next-line no-param-reassign
    acc[type][year] = acc[type][year] || [];
    acc[type][year].push({timeframe, date});

    return acc;
  },
  {previous: {}, current: [], upcoming: {}}
);

type Quarter = {timeframe: string; dateRange: string};

interface QuartersOutput {
  previousQuarters: {[year: string]: Quarter[]};
  currentQuarters: Quarter[];
  archivedQuarters: {[year: string]: Quarter[]};
}

export function generateQuarterData(): QuartersOutput {
  const result: QuartersOutput = {
    previousQuarters: {},
    currentQuarters: [],
    archivedQuarters: {},
  };

  const getDateRange = (quarter: number, year: number): string => {
    const startMonth = (quarter - 1) * 3;
    const start = new Date(year, startMonth, 1);
    const end = new Date(year, startMonth + 3, 0); // Last day of quarter
    return `${start.toLocaleString('default', {month: 'short'})} ${start.getDate()} - ${end.toLocaleString('default', {month: 'short'})} ${end.getDate()}`;
  };

  const createQuarterObject = (quarter: number, year: number): Quarter => {
    return {
      timeframe: `Q${quarter} FY${year - 2000}`,
      dateRange: getDateRange(quarter, year),
    };
  };

  // Generate current quarters: previous, current, and next, and the quater after next quater, and three quarter ahead
  const currentQuarters = [];
  const previousQuarter = ((CURRENT_QUARTER - 2 + 4) % 4) + 1; // Previous quarter
  const nextQuarter = (CURRENT_QUARTER % 4) + 1; // Next quarter
  const quarterAfterNext = (nextQuarter % 4) + 1; // Quarter after next quarter
  const quarterThreeAhead = (quarterAfterNext % 4) + 1; // Three quarter ahead

  const currentYearAdjustments = [
    {quarter: previousQuarter, year: CURRENT_QUARTER === 1 ? CURRENT_YEAR - 1 : CURRENT_YEAR},
    {quarter: CURRENT_QUARTER, year: CURRENT_YEAR},
    {quarter: nextQuarter, year: CURRENT_QUARTER === 4 ? CURRENT_YEAR + 1 : CURRENT_YEAR},
    {quarter: quarterAfterNext, year: nextQuarter === 4 ? CURRENT_YEAR + 1 : CURRENT_YEAR},
    {quarter: quarterThreeAhead, year: quarterAfterNext === 4 ? CURRENT_YEAR + 1 : CURRENT_YEAR},
  ];

  currentQuarters.push(
    ...currentYearAdjustments.map((q) => createQuarterObject(q.quarter, q.year))
  );
  result.currentQuarters = currentQuarters;

  // Generate previous quarters
  const previousQuarterInfo = currentYearAdjustments[0];
  const {quarter: prevQuarterNum, year: prevQuarterYear} = previousQuarterInfo;

  // Include quarters before the previous quarter in the same year
  result.previousQuarters[prevQuarterYear] = [];
  for (let q = 1; q <= prevQuarterNum; q++) {
    result.previousQuarters[prevQuarterYear].push(createQuarterObject(q, prevQuarterYear));
  }

  // Generate archived quarters: years before the year of previousQuarters down to 2023
  for (let year = prevQuarterYear - 1; year >= 2023; year--) {
    result.archivedQuarters[year] = [];
    for (let q = 1; q <= 4; q++) {
      result.archivedQuarters[year].push(createQuarterObject(q, year));
    }
  }

  // remove Q1 and Q2 for 2023 in the archivedQuaters, which is the first indexc and second index
  if (result.archivedQuarters[2023]) {
    result.archivedQuarters[2023].splice(0, 2);
  }

  return result;
}

export const getDefaultTeamsRoute = (): string => {
  return ROUTE_PATHS.TEAMS;
};

export const getTeamsAppsRoute = (serviceId?: number): string =>
  serviceId === undefined
    ? ROUTE_PATHS.TEAMS_APPS
    : ROUTE_PATHS.TEAMS_APP.replace(':serviceId', String(serviceId));

export const getComponentsAndServiceReferenceRoute = (
  componentId?: number,
  referenceId?: number
): string => {
  if (componentId === undefined) {
    return ROUTE_PATHS.TEAMS_COMPONENTS_AND_SERVICES;
  }

  const componentRoute = ROUTE_PATHS.TEAMS_COMPONENTS_AND_SERVICES_COMPONENT.replace(
    ':componentId',
    String(componentId)
  );

  if (referenceId === undefined) {
    return componentRoute;
  }

  return ROUTE_PATHS.TEAMS_COMPONENTS_AND_SERVICES_REFERENCE.replace(
    ':componentId',
    String(componentId)
  ).replace(':referenceId', String(referenceId));
};

export const getComponentsAndServicesServiceRoute = (
  componentId: number,
  serviceId: number
): string =>
  ROUTE_PATHS.TEAMS_COMPONENTS_AND_SERVICES_SERVICE.replace(
    ':componentId',
    String(componentId)
  ).replace(':serviceId', String(serviceId));

const safeKnowledgeCategorySlug = (categorySlug: string) =>
  encodeURIComponent(categorySlug.trim().toLowerCase());

export const getKnowledgeCategoryPath = (categorySlug: string): string =>
  ROUTE_PATHS.KNOWLEDGE_CATEGORY.replace(':category', safeKnowledgeCategorySlug(categorySlug));

export const getKnowledgePagePath = (categorySlug: string, id: number | string): string =>
  ROUTE_PATHS.KNOWLEDGE_PAGE.replace(':category', safeKnowledgeCategorySlug(categorySlug)).replace(
    ':id',
    String(id)
  );

export const getKnowledgeGuidePath = (guideId: string): string =>
  ROUTE_PATHS.KNOWLEDGE_GUIDE.replace(':guideId', encodeURIComponent(guideId));

export const isDateWithinCurrentQuarter = (date: Date): boolean => {
  const quarter = date.getMonth() < 3 ? 1 : date.getMonth() < 6 ? 2 : date.getMonth() < 9 ? 3 : 4;

  return quarter === CURRENT_QUARTER && date.getFullYear() === CURRENT_YEAR;
};

export const isDateAfterCurrentQuarter = (date: Date): boolean => {
  const quarter = date.getMonth() < 3 ? 1 : date.getMonth() < 6 ? 2 : date.getMonth() < 9 ? 3 : 4;

  // If the year is greater than current year, it's definitely after current quarter
  if (date.getFullYear() > CURRENT_YEAR) {
    return true;
  }

  // If the year is less than current year, it's definitely before current quarter
  if (date.getFullYear() < CURRENT_YEAR) {
    return false;
  }

  // Same year, compare quarters
  return quarter > CURRENT_QUARTER;
};

export const isDateBeforeCurrentQuarter = (date: Date): boolean => {
  const quarter = date.getMonth() < 3 ? 1 : date.getMonth() < 6 ? 2 : date.getMonth() < 9 ? 3 : 4;

  // If the year is less than current year, it's definitely before current quarter
  if (date.getFullYear() < CURRENT_YEAR) {
    return true;
  }

  // If the year is greater than current year, it's definitely after current quarter
  if (date.getFullYear() > CURRENT_YEAR) {
    return false;
  }

  // Same year, compare quarters
  return quarter < CURRENT_QUARTER;
};
